import {
  getRequestGeneric,
  postRequestGeneric,
  putRequestGeneric,
} from '@/services/backendApiGeneric'

// ---------------------------------------------------------------------------
// Types — mirror the Go DTOs in internal/billing/apps_*.go. All money values
// are NET EUR cents (zzgl. USt.).
// ---------------------------------------------------------------------------

export type PricingModel = 'seat_tier' | 'staffel'

export interface AppRole {
  key: string
  label: string
}

/**
 * KARS-Schaden-Rolle aus tbl_roles — gemeinsame Datenquelle mit der
 * Berechtigungen-Seite in KARS-Schaden (/berechtigungen).
 */
export interface RoleOption {
  id: number
  name: string
  isSystem: boolean
}

/**
 * Preisangaben einer App, bereits für die Geschäftsart der Organisation
 * aufgelöst (Preismodell 31.10.2026, kein Einführungspreis).
 *
 * - seat_tier (KARS-Schaden): Grundpreis je Standort, darin enthaltene Nutzer
 *   (baseBundleUsers) und Betrag je weiterem Nutzer (extraUserCents).
 * - staffel (KARS-HR): Grundpreis je Standort plus Betrag je Mitarbeiter
 *   (perUserCents), ab dem reducedFromUser-ten Mitarbeiter eines Standorts
 *   der reduzierte Betrag (perUserReducedCents).
 */
export interface AppPricingHint {
  model: PricingModel
  /** Grundpreis je Standort und Monat, in Cent. */
  baseAmountCents?: number
  /** Im Grundpreis enthaltene Nutzer (nur seat_tier). */
  baseBundleUsers?: number
  /** Betrag je Nutzer über baseBundleUsers hinaus, in Cent (nur seat_tier). */
  extraUserCents?: number
  /** Betrag je Mitarbeiter, in Cent (nur staffel). */
  perUserCents?: number
  /** Reduzierter Betrag je Mitarbeiter ab reducedFromUser, in Cent (nur staffel). */
  perUserReducedCents?: number
  /** Ab diesem Mitarbeiter eines Standorts (1-basiert) gilt perUserReducedCents. */
  reducedFromUser?: number
}

export interface AppCatalogEntry {
  key: string
  name: string
  tagline: string
  accent?: string
  logo?: string
  roles: AppRole[]
  pricingModel: PricingModel
  available: boolean
  billable: boolean
  pricing: AppPricingHint
}

export interface GridLocation {
  id: number
  name: string
  /** Number of employees expected at this location (set during registration). */
  expectedUsers?: number
  /** Preisrelevante Geschäftsart des Standorts (teuerste Standortrolle). */
  businessType?: string
}

export interface GridUser {
  id: number
  firstName: string
  lastName: string
  email: string
  userRole: string
  /** KARS-Schaden-Rolle (tbl_roles), null = keine Zuweisung. */
  idRole: number | null
  locationIds: number[]
}

export interface AppAssignment {
  appKey: string
  idLocation: number
  idUser: number
  appRole: string
  status: string
}

export interface AppSubscription {
  id: number
  idOrganization: number
  /** Unternehmen des Abos; null = organisationsweites Abo (Spec 2026-10-21). */
  idCompany: number | null
  appKey: string
  stripeSubscriptionId: string | null
  status: string
  trialEndsAt: string | null
  currentPeriodStart: string | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  licensedUsers: number
}

export interface LocationAppQuote {
  locationId: number
  /** Geschäftsart, nach der dieser Standort bepreist wurde. */
  businessType: string
  userCount: number
  regularCents: number
  /** Seit dem Preismodell 31.10.2026 immer gleich regularCents (kein Einführungspreis). */
  introCents: number
  /** Immer 0 (kein Einführungspreis mehr); bleibt aus API-Kompatibilität. */
  introMonths: number
}

export interface AppQuote {
  appKey: string
  name: string
  userCount: number
  regularCents: number
  introCents: number
  introMonths: number
  locations: LocationAppQuote[]
  /**
   * false für verfügbare, aber nicht über KARS abgerechnete Apps
   * (z. B. KARS-Verträge): erscheinen im Warenkorb mit 0 €.
   */
  billable: boolean
  /**
   * Ende der Einführungsphase eines Bestandsabos mit Einführungs-Zeitplan
   * (Buchung bis 31.10.2026), falls bekannt. Neubuchungen haben keine.
   */
  introEndsAt?: string | null
}

export interface Quote {
  businessType: string
  apps: AppQuote[]
  billableUsers: number
  totalRegularCents: number
  /** Immer gleich totalRegularCents (kein Einführungspreis mehr). */
  totalIntroCents: number
  /** Immer false seit dem Preismodell 31.10.2026. */
  hasIntro: boolean
  /**
   * Startangebot 2026 (Platzvergabe am 31.10.2026 beendet): Ende der
   * kostenfreien Nutzung neuer Buchungen (ISO-Zeitpunkt, 01.01.2027 00:00
   * deutscher Zeit). Nur bei Unternehmen, die noch einen der 50 Plätze
   * bekommen haben, sonst gilt die 30-Tage-Testphase.
   */
  freeTrialUntil?: string | null
  /**
   * Nur bei einem über die Organisation abgerechneten Unternehmen
   * (?idCompany=): sein eigener Anteil. Die Quote selbst nennt dann den
   * Organisationspreis, den der Checkout bucht (Spec 2026-10-21).
   */
  companyShare?: Quote
}

export interface AppsGridResponse {
  businessType: string
  /**
   * Organisation ist von der Bezahlung befreit: Kalkulation ist genullt und
   * das Speichern führt nie zu Stripe (Spec 2026-09-14).
   */
  paywallExempt: boolean
  /**
   * Einzeln befreite Apps (Super-Admin, 22.09.26 Teil 2, XIV-Nachtrag):
   * kostenfrei ohne Stripe; alle übrigen Apps werden normal gebucht.
   */
  exemptApps: string[]
  apps: AppCatalogEntry[]
  /** KARS-Schaden-Rollen der Organisation für das Rollen-Dropdown. */
  roleOptions: RoleOption[]
  locations: GridLocation[]
  users: GridUser[]
  assignments: AppAssignment[]
  subscriptions: AppSubscription[]
  quote: Quote
}

/** Wire format for an assignment in price-preview / save requests. */
export interface AssignmentInput {
  appKey: string
  idLocation: number
  idUser: number
  appRole: string
  /** KARS-Schaden-Rolle (tbl_roles); nur für appKey "kars", null = unverändert. */
  idRole?: number | null
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** Coerce a possibly-null Go slice (empty slices marshal as JSON null) to an array. */
function arr<T> (v: T[] | null | undefined): T[] {
  return Array.isArray(v) ? v : []
}

/** Ensure a quote (and its company share) always carries an apps array. */
function normalizeQuote (q: Quote | null | undefined): Quote {
  const { companyShare, ...rest } = q ?? ({} as Quote)
  const out: Quote = { ...rest, apps: arr(q?.apps) }
  return companyShare ? { ...out, companyShare: normalizeQuote(companyShare) } : out
}

/** Ensure every grid collection is an array even when the backend sends null. */
function normalizeGrid (g: AppsGridResponse): AppsGridResponse {
  return {
    ...g,
    paywallExempt: !!g?.paywallExempt,
    exemptApps: arr(g?.exemptApps),
    apps: arr(g?.apps),
    roleOptions: arr(g?.roleOptions),
    locations: arr(g?.locations),
    users: arr(g?.users),
    assignments: arr(g?.assignments),
    subscriptions: arr(g?.subscriptions),
    quote: normalizeQuote(g?.quote),
  }
}

/** `?idCompany=…` für Aufrufe im Scope eines Unternehmens (Spec 2026-10-21). */
function companyQuery (idCompany?: number | null): string {
  return idCompany ? `?idCompany=${encodeURIComponent(idCompany)}` : ''
}

/** Full grid payload: structure, current assignments, subscriptions, quote. */
export async function fetchAppsGrid (idCompany?: number | null): Promise<AppsGridResponse> {
  return normalizeGrid(await getRequestGeneric(`/protected/apps/grid${companyQuery(idCompany)}`))
}

/** Live price preview for a proposed assignment set (nothing is persisted). */
export async function previewAppsPrice (assignments: AssignmentInput[], idCompany?: number | null): Promise<Quote> {
  return normalizeQuote(
    await postRequestGeneric(`/protected/apps/price-preview${companyQuery(idCompany)}`, { assignments }, true),
  )
}

/**
 * Persist the assignment set. Administrator only. Mit idCompany ersetzt das
 * Backend nur die Zuweisungen dieses Unternehmens. HTTP 409, wenn ein
 * Unternehmen mit eigener Abrechnung neue kostenpflichtige Apps bekäme, aber
 * noch nicht eingerichtet ist (Meldung des Backends nennt das Unternehmen).
 */
export async function saveAppsAssignments (
  assignments: AssignmentInput[],
  idCompany?: number | null,
): Promise<{ assignments: AppAssignment[], quote: Quote, subscriptions: AppSubscription[] }> {
  const resp = await putRequestGeneric(`/protected/apps/assignments${companyQuery(idCompany)}`, { assignments }, true)
  return {
    assignments: arr(resp?.assignments),
    quote: normalizeQuote(resp?.quote),
    // Bei Paywall-Befreiung legt das Speichern Freilizenzen an – die
    // aktualisierte Abo-Liste kommt direkt mit (Spec 2026-09-14).
    subscriptions: arr(resp?.subscriptions),
  }
}

/**
 * Grund, aus dem eine Buchung im Organisations-Scope ein Unternehmen mit
 * eigener Abrechnung übersprungen hat (backend company_checkout.go).
 */
export type CompanySkipReason = 'billing_not_set_up' | 'no_payment_method' | 'error' | 'busy'

export interface CompanySkip {
  idCompany: number
  /** Unbekannte Gründe eines neueren Backends bleiben als string erhalten. */
  reason: CompanySkipReason | string
}

/** Abo-Start der Unternehmen mit eigener Abrechnung (nur im Organisations-Scope befüllt). */
export interface OwnBillingOutcome {
  startedCompanies: number[]
  skipped: CompanySkip[]
}

/** Antwort von POST /apps/checkout. */
export interface CheckoutResult extends OwnBillingOutcome {
  /** In diesem Aufruf angelegte Abos. */
  started: AppSubscription[]
  /** Alle Abos der Organisation nach dem Aufruf. */
  subscriptions: AppSubscription[]
}

/** Antwort von POST /apps/checkout-session. */
export interface CheckoutSessionResult extends OwnBillingOutcome {
  /** Stripe-Checkout-URL; leer = ohne Stripe weiter. */
  url: string
}

function ownBillingOutcome (resp: Partial<OwnBillingOutcome> | null | undefined): OwnBillingOutcome {
  return { startedCompanies: arr(resp?.startedCompanies), skipped: arr(resp?.skipped) }
}

/**
 * Start 30-day trials for any newly selected paid apps. Administrator only.
 * `checkoutSessionId`: Rückkehr aus dem Setup-Checkout von
 * getAppsCheckoutSessionUrl – das Backend übernimmt zuerst die dort
 * hinterlegte Zahlungsmethode.
 */
export async function checkoutApps (checkoutSessionId?: string, idCompany?: number | null): Promise<CheckoutResult> {
  const body = checkoutSessionId ? { checkoutSessionId } : {}
  const resp = await postRequestGeneric(`/protected/apps/checkout${companyQuery(idCompany)}`, body, true) as Partial<CheckoutResult> | null
  return {
    started: arr(resp?.started),
    subscriptions: arr(resp?.subscriptions),
    ...ownBillingOutcome(resp),
  }
}

/**
 * Zahlungsmethoden-Pflicht der Organisation (GET /apps/payment-requirement).
 * `required`: kostenpflichtige Apps zugewiesen, aber keine Zahlungsmethode.
 * `activationPending`: Zahlungsmethode da, Abos fehlen noch → checkoutApps().
 */
export interface PaymentRequirement {
  required: boolean
  activationPending: boolean
  pendingApps: string[]
  /** Pausierte Abos mit offener Rechnung: „Zahlung erforderlich“. */
  paymentDue: PaymentDueItem[]
}

/**
 * Pausiertes Abo, das erst nach Bezahlung der offenen Rechnung weiterläuft
 * (Backend: PaymentDue). hostedInvoiceUrl ist der Stripe-Link zur Rechnung;
 * null, wenn keiner geliefert wurde oder er nicht https ist.
 */
export interface PaymentDueItem {
  appKey: string
  subscriptionId: string
  message: string
  hostedInvoiceUrl: string | null
}

const PAYMENT_DUE_MESSAGE = 'Zahlung erforderlich'

/** Liest paymentDue defensiv: nur Einträge mit appKey, Links nur https. */
export function paymentDueItems (raw: unknown): PaymentDueItem[] {
  if (!Array.isArray(raw)) {
    return []
  }
  const out: PaymentDueItem[] = []
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') {
      continue
    }
    const e = entry as Record<string, unknown>
    if (typeof e.appKey !== 'string' || e.appKey === '') {
      continue
    }
    const url = typeof e.hostedInvoiceUrl === 'string' && e.hostedInvoiceUrl.startsWith('https://') ? e.hostedInvoiceUrl : null
    out.push({
      appKey: e.appKey,
      subscriptionId: typeof e.subscriptionId === 'string' ? e.subscriptionId : '',
      message: typeof e.message === 'string' && e.message ? e.message : PAYMENT_DUE_MESSAGE,
      hostedInvoiceUrl: url,
    })
  }
  return out
}

/** Administrator only. Mit idCompany die Zahlungspflicht dieses Unternehmens. */
export async function getAppsPaymentRequirement (idCompany?: number | null): Promise<PaymentRequirement> {
  const resp = await getRequestGeneric(`/protected/apps/payment-requirement${companyQuery(idCompany)}`) as Partial<PaymentRequirement> | null
  return {
    required: resp?.required === true,
    activationPending: resp?.activationPending === true,
    pendingApps: arr(resp?.pendingApps),
    paymentDue: paymentDueItems(resp?.paymentDue),
  }
}

/**
 * Rückkehrziel des Setup-Checkouts; 'company-billing' und 'users'
 * (Benutzerverwaltung des Unternehmens, Spec 2026-10-22) nur mit idCompany.
 */
export type CheckoutReturn = 'onboarding' | 'settings' | 'company-billing' | 'users'

/**
 * Bucht die gespeicherten App-Zuweisungen (je App ein Abo). Ist schon eine
 * Zahlungsmethode hinterlegt, legt das Backend die Abos sofort an und liefert
 * einen leeren String. Sonst kommt die URL eines Stripe-Checkouts, der nur
 * die Zahlungsmethode einsammelt; die Rückkehr trägt `checkout_session` und
 * wird mit settlePaymentRequirement(sessionId) abgeschlossen. `returnPath`
 * steuert das Rückkehrziel ('onboarding', 'settings' oder mit idCompany
 * 'company-billing'). Im Organisations-Scope startet das Backend auf dem Weg
 * ohne Stripe auch die Abos der Unternehmen mit eigener Abrechnung
 * (startedCompanies, skipped).
 */
export async function startAppsCheckoutSession (
  returnPath: CheckoutReturn = 'onboarding',
  idCompany?: number | null,
): Promise<CheckoutSessionResult> {
  try {
    const data = await postRequestGeneric(
      `/protected/apps/checkout-session${companyQuery(idCompany)}`, { returnPath }, true,
    ) as Partial<CheckoutSessionResult> | null
    return { url: data?.url ?? '', ...ownBillingOutcome(data) }
  } catch (error) {
    throw withCheckoutCause(error)
  }
}

/** Wie startAppsCheckoutSession, liefert nur die Checkout-URL. */
export async function getAppsCheckoutSessionUrl (returnPath: CheckoutReturn = 'onboarding', idCompany?: number | null): Promise<string> {
  return (await startAppsCheckoutSession(returnPath, idCompany)).url
}

/**
 * Bei einem Serverfehler im Checkout zeigt die Pauschalmeldung nicht, woran
 * es liegt (Stripe-Kunde, Preis, Zahlungsmethode …). Die Ursache aus dem
 * Backend wird daher in die Meldung übernommen. Abgelaufene Sitzung und
 * fehlende Berechtigung behalten ihre eigenen Texte.
 */
function withCheckoutCause (error: unknown): unknown {
  const err = error as { message?: string, errorCode?: number, originalMessage?: string } | null
  const cause = typeof err?.originalMessage === 'string' ? err.originalMessage.trim() : ''
  if (!err || !cause || err.errorCode === 401 || err.errorCode === 403) {
    return error
  }
  return { ...err, message: `Der Bezahlvorgang konnte nicht gestartet werden: ${cause}` }
}

/** Format NET EUR cents as a localized "12,34 €" string. */
export function formatCents (cents: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  }).format((cents || 0) / 100)
}
